<template>
  <div class="content mb-5">
    <b-row class="mb-5">
      <b-col>
        <t-data-table
          data-test-id="sites-data-table"
          with-filter
          hoverable
          :filter-placeholder="$t('Search Locations')"
          :title="this.$t('All Locations')"
          :loading="loading"
          :fetching="fetching"
          :table-data="dataSet"
          :actions="tableActions"
          :server-items-length="serverItemsLength"
          user-role="customer"
          @on-delete="onDelete"
          @on-add="onAdd"
          @on-export="onExport"
          @on-row-clicked="onRowClicked"
          :searchExports="searchExports"
          @export-headers="exportedHeaders"
          @async-paginate="onSearch"
          :displayWarningWhenCantDelete="true"
          selectableKey="deletable"
        />
      </b-col>
    </b-row>
    <router-view></router-view>
  </div>
</template>

<route>
{
  "name": "Customer Environment Locations",
  "meta": {
    "authorize" : [1, 3, 4]
  }
}
</route>

<script>
import { mapMutations, mapActions, mapGetters } from "vuex";

export default {
  layout: "customer",
  data: function () {
    return {
      loading: false,
      fetching: false,
      currentHeaders: null,
      serverItemsLength: 0,
      dataOptions: {
        results: 10,
        page: 1,
        sortBy: "name",
        sortDesc: "asc",
        filter: null,
      },
      tableActions: {
        add: {
          enabled: true,
          actions: [
            {
              text: this.$t("Static IP"),
              value: {
                name: this.$t("Static IP"),
                modal: "t-location-static-ip",
                type: "staticip",
              },
            },
            {
              text: this.$t("Dynamic IP"),
              value: {
                name: this.$t("Dynamic IP"),
                modal: "t-location-dynamic-ip",
                type: "dynamicip",
              },
            },
            {
              text: this.$t("Dynamic DNS"),
              value: {
                name: this.$t("Dynamic DNS"),
                modal: "t-location-dynamic-dns",
                type: "dynamicdns",
              },
            },
            {
              text: this.$t("Roaming"),
              value: {
                name: this.$t("Roaming"),
                modal: "t-location-roaming",
                type: "roaming",
              },
            },
            {
              text: this.$t("Virtual"),
              value: {
                name: this.$t("Virtual"),
                modal: "t-location-virtual",
                type: "virtual",
              },
            },
          ],
        },
        import: { enabled: false },
        export: { enabled: true },
        delete: { enabled: true },
      },
      searchExports: ["name", "type", "policy_name"],
      dataSet: {
        fields: [
          { value: "name", text: this.$t("Location Name"), sortable: true },
          { value: "id", text: this.$t("Location ID"), sortable: true },
          { value: "type", text: this.$t("Type"), sortable: true },
          {
            value: "policy_name",
            text: this.$t("Policy"),
            sortable: true,
          },
          { value: "ip", text: this.$t("IP Address"), sortable: true },
          {
            value: "accesstime",
            text: this.$t("Access Time"),
            sortable: false,
          },
        ],
        items: [],
      },
    };
  },
  watch: {
    locations: {
      handler() {
        this.dataSet.items = this.locations.all.data.map((location) => {
          return {
            ...location,
            isSelectable: location.deletable,
          };
        });
      },
      deep: true,
      immediate: true,
    },
    serverTotal: {
      handler() {
        this.serverItemsLength = this.serverTotal;
      },
      immediate: true,
    },
  },
  computed: {
    ...mapGetters("customerPolicies", ["customerPolicies"]),
    ...mapGetters("locations", [
      "locations",
      "serverTotal",
      "exportAllLocations",
    ]),
    ...mapGetters("authentication", ["selectedUser"]),
  },
  methods: {
    ...mapMutations("modal", ["createModal", "showModal"]),
    ...mapActions("customerPolicies", ["fetchCustomerPolicies"]),
    ...mapActions("locations", [
      "fetchLocations",
      "deleteLocations",
      "fetchExportLocations",
    ]),
    ...mapActions("pageMeta", ["setPageTitle", "setPageSubtitle"]),
    ...mapActions("toast", ["displayToast"]),
    async onDelete(locations) {
      try {
        this.loading = true;
        let onlyDeletables = locations.filter(
          (location) => location.deletable === true
        );
        await this.deleteLocations({
          userId: this.selectedUser.id,
          locations: onlyDeletables,
        });
        this.displayToast({
          title: this.$t("Success"),
          message: this.$t("Deleted: X", { item: locations.length }),
          duration: 2000,
          variant: "success",
        });
      } catch (err) {
        // stub error
      } finally {
        this.loading = false;
      }
    },
    onAdd({ modal, type }) {
      this.createModal({
        modal,
        dataTestId: `modal-add-domain-${type}`,
        title: `${this.$t("Add Location")} (${
          this.$sitesTypeLookup[`${type}`]
        })`,
        subtitle: this.selectedUser.accountname,
        modalData: {
          type,
          customerPolicies: this.customerPolicies,
        },
        props: {
          size: "lg",
          okTitle: this.$t("Add"),
        },
      });

      this.showModal();
    },
    exportedHeaders(headers) {
      this.currentHeaders = headers;
    },
    async onExport() {
      try {
        this.fetching = true;
        await this.fetchExportLocations({
          id: this.selectedUser.id,
        });
      } finally {
        this.fetching = false;
      }

      if (this.exportAllLocations.length) {
        const exportData = this.exportAllLocations.map((res) => {
          let payload = {};
          this.currentHeaders.forEach((headers) => {
            return (payload[headers.value] = res[headers.value]);
          });
          return payload;
        });
        this.$downloadCSV(exportData, "locations");
      } else {
        this.displayToast({
          title: this.$t("Failure"),
          message: this.$t("No location in table"),
          duration: 2000,
          variant: "danger",
        });
      }
    },
    onRowClicked(item) {
      this.$router.push({
        path: `/customer/environment/locations/${item.id}?type=${item.type}`,
      });
    },
    async onSearch(options) {
      if (options.itemsPerPage === undefined) return;
      if (options.itemsPerPage) {
        this.dataOptions.results = options.itemsPerPage;
      }
      if (options.page) {
        this.dataOptions.page = options.page;
      }
      if (options.sortBy) {
        this.dataOptions.sortBy = options.sortBy[0];
      }
      if (options.sortDesc) {
        this.dataOptions.sortDesc = options.sortDesc;
      }

      if (this.dataOptions.filter !== options.filter) {
        this.dataOptions.page = 1;
      }
      this.dataOptions.filter = options.filter;

      try {
        await this.fetchLocations({
          id: this.selectedUser.id,
          type: "all",
          ...this.dataOptions,
        });
      } finally {
        this.loading = false;
      }
    },
  },
  async created() {
    this.setPageTitle(this.$t("Locations"));
    this.setPageSubtitle([
      this.$t(
        "Locations identify all your organization's network locations so content filtering can be applied to them using a policy."
      ),
    ]);
    await Promise.all([
      this.fetchCustomerPolicies({
        id: this.selectedUser.id,
        results: 10,
        sortDesc: [false],
      }),
    ]);

    this.loading = false;
  },
};
</script>
